/**
 * Wave 379F · CP-005 · HousyAvatar — Floating Concierge.
 *
 * 2026-05-02 CP-005 fidelity rebuild.
 *
 * Source-of-truth lamina:
 *   /Users/jefersonpropheta/Downloads/housy_floating_concierge_single_html.html
 *
 * What changed from the prior 100×100 build:
 *   - viewBox upgraded 100×100 → 240×240 (mockup canonical).
 *   - All silhouette paths re-anchored to mockup coordinates:
 *       body  : M58 103 C61 73 83 57 120 57 …
 *       roof  : M37 113 C70 81 94 57 120 43 …
 *       chimney : x=156 y=61 w=22 h=45 (rx 5)
 *       eyes  : 83×126 / 137×126 (20×25 rx 5), pupils 88×130 / 142×130
 *       mouth : 104×158 (32×25 rx 8) shell + amber tongue + shine
 *       badge : cx=176 cy=64 r=25 outer / r=20 inner
 *       halo  : cx=120 cy=197 rx=56 outer ring / rx=38 inner shimmer
 *       listening rings : M53 111 / M187 111
 *       thought bubble : M132 34 (top-right, 3 dots cy=52)
 *       error sparks : 4 strokes 185×91 / 197×101 / 63×91 / 51×101
 *       zzz : 176×51 / 190×39 / 207×24 (z, Z, Z)
 *   - Palette locked to mockup CSS vars:
 *       body   : #E6DCFF → #A68AFF → #5F45D8 → #241847
 *       roof   : #25284F → #12152F (light: #4B517B → #252843)
 *       eye    : #FFF7D8 → #FFD49B → #FF9F2E
 *       mouth shell #2A1934, amber tongue #FF9F2E
 *       halo outer #A76DFF stroke=5, inner #B580FF
 *       listen ring #A98CFF stroke=4
 *       error spark #FF554A stroke=4
 *       thought bubble #E6DCFF, dots #40376F
 *       zzz #EEE8FF
 *   - Drag-physics expanded from rotation-only to FOUR layers
 *     (per mockup script):
 *       • body squash/stretch (scaleX 1+x, scaleY 1−x*0.66)
 *       • halo deformation (translate X + scaleX/scaleY inverse)
 *       • roof lag (opposite-sign translate X + Y, slow spring)
 *       • badge lag (heaviest, opposite-sign translate X + Y)
 *     All driven by Framer Motion springs from two new optional
 *     velocity props (`externalVelocityX`, `externalVelocityY`).
 *     Existing `externalTiltX` keeps driving the primary tilt rotation.
 *
 * Public API preserved verbatim. Two new optional props added
 * (velocityX/Y); both default to 0 so existing call sites work
 * unchanged.
 *
 * HouseAgent integration in this same patch is a 4-line additive
 * wire-through — vx + vy captured from `info.velocity` during onDrag,
 * snapped to 0 on onDragEnd, passed to HousyAvatar.
 */

import {
  AnimatePresence,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useSpring,
  useTransform,
  type MotionValue,
  type Transition,
} from 'framer-motion';
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { cn } from '@/lib/cn';

export type HousyState =
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'error'
  | 'sleeping';

export type HousyMode = 'light' | 'dark' | 'auto';

export interface HousyAvatarProps {
  state?: HousyState;
  unreadCount?: number;
  mode?: HousyMode;
  size?: number;
  draggable?: boolean;
  onClick?: () => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  onPointerUp?: (e: React.PointerEvent) => void;
  className?: string;
  reduceMotion?: boolean;
  sleepAfterMs?: number;
  externalTiltX?: number | MotionValue<number>;
  /**
   * CP-005 · drag velocity X in px/s (positive = right).
   * When provided (typically a MotionValue from a parent's `info.velocity.x`
   * during `onDrag`), drives badge X-lag, halo translation, roof X-lag,
   * and body squash on the X axis. Defaults to 0. Internally clamped
   * so callers don't have to tune the pixel budget.
   */
  externalVelocityX?: number | MotionValue<number>;
  /**
   * CP-005 · drag velocity Y in px/s (positive = down).
   * Drives badge Y-lag, roof Y-lag, body squash on the Y axis.
   */
  externalVelocityY?: number | MotionValue<number>;
}

const SPRING_BODY: Transition = { type: 'spring', stiffness: 170, damping: 19, mass: 0.95 };
const SPRING_TIGHT: Transition = { type: 'spring', stiffness: 280, damping: 24, mass: 0.7 };

/**
 * CP-005 helper · subscribe to a number-or-MotionValue prop and mirror
 * the latest into a single internal MotionValue. Lets the rest of the
 * file work against one shape regardless of caller form.
 */
function useExternalNumber(input: number | MotionValue<number> | undefined): MotionValue<number> {
  const isMV = input !== undefined && typeof input !== 'number';
  const internal = useMotionValue(typeof input === 'number' ? input : 0);
  useMotionValueEvent(
    isMV ? (input as MotionValue<number>) : internal,
    'change',
    (latest) => {
      if (isMV) internal.set(latest as number);
    },
  );
  useEffect(() => {
    if (typeof input === 'number') internal.set(input);
  }, [input, internal]);
  return internal;
}

export function HousyAvatar({
  state = 'idle',
  unreadCount = 0,
  mode = 'auto',
  size = 72,
  draggable: _draggable = false,
  onClick,
  onPointerDown,
  onPointerUp,
  className,
  reduceMotion,
  sleepAfterMs = 12_000,
  externalTiltX,
  externalVelocityX,
  externalVelocityY,
}: HousyAvatarProps) {
  const prefersReduced = useReducedMotion() ?? false;
  const reduced = reduceMotion ?? prefersReduced;

  // ── Internal sleep timer ───────────────────────────────────────
  const [internalSleep, setInternalSleep] = useState(false);
  useEffect(() => {
    if (sleepAfterMs <= 0) return;
    if (state === 'sleeping') return;
    let timer: number | undefined;
    const wake = () => {
      if (internalSleep) setInternalSleep(false);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!document.hidden) setInternalSleep(true);
      }, sleepAfterMs);
    };
    const onVis = () => {
      if (document.hidden) window.clearTimeout(timer);
      else wake();
    };
    window.addEventListener('pointermove', wake, { passive: true });
    window.addEventListener('keydown', wake);
    document.addEventListener('visibilitychange', onVis);
    wake();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', wake);
      window.removeEventListener('keydown', wake);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [internalSleep, sleepAfterMs, state]);
  const effectiveState: HousyState =
    state === 'sleeping' ? 'sleeping' : internalSleep ? 'sleeping' : state;

  // ── Wave 380 · FIX 4 · floating emoji reaction overlay ──────────
  // A small decorative glyph that floats above Housy's chimney to add
  // a personality "tic" on top of the SVG cues we already render
  // (thought-bubble, listening rings, zZz, error sparks). Decorative
  // only — never replaces the silhouette state. Skipped under
  // prefers-reduced-motion so it doesn't disturb users who opted out.
  //
  // Mapping:
  //   thinking  → 💭  (2 s delay; hides on quick replies)
  //   listening → 👂  (instant on input focus)
  //   sleeping  → 💤  (instant)
  //   error     → ⚠️  (instant; rides the orb's own shake)
  //   speaking, idle → none (orb already expresses these)
  const [reactionEmoji, setReactionEmoji] = useState<string | null>(null);
  useEffect(() => {
    if (reduced) {
      // Honor prefers-reduced-motion: no overlay glyph.
      setReactionEmoji(null);
      return;
    }
    const map: Partial<Record<HousyState, string>> = {
      thinking: '💭',
      listening: '👂',
      sleeping: '💤',
      error: '⚠️',
    };
    const next = map[effectiveState] ?? null;
    if (effectiveState === 'thinking') {
      // Delay 2 s so quick assistant replies don't flash a thought
      // cloud the user barely registers. If the state moves off
      // 'thinking' before the delay elapses the timer is cleared and
      // no emoji ever appears for that round.
      setReactionEmoji(null);
      const t = window.setTimeout(() => setReactionEmoji(next), 2000);
      return () => window.clearTimeout(t);
    }
    setReactionEmoji(next);
  }, [effectiveState, reduced]);

  // ── Eye tracking (pupils follow pointer) ──────────────────────
  // Mockup eye-rect is 20×25 in 240-space; pupil rect is 8×8. Allow
  // pupil to slide ±2.4 px on X, ±1.4 px on Y inside its eye-rect.
  const lookX = useMotionValue(0);
  const lookY = useMotionValue(0);
  const pupilX = useSpring(lookX, { stiffness: 180, damping: 22 });
  const pupilY = useSpring(lookY, { stiffness: 180, damping: 22 });
  const pupilTX = useTransform(pupilX, [-1, 1], [-2.4, 2.4]);
  const pupilTY = useTransform(pupilY, [-1, 1], [-1.4, 1.4]);

  const wrapperRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (
      effectiveState === 'sleeping' ||
      effectiveState === 'error' ||
      effectiveState === 'thinking'
    ) {
      lookX.set(0);
      lookY.set(0);
      return;
    }
    const el = wrapperRef.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / (window.innerWidth / 2);
      const dy = (e.clientY - cy) / (window.innerHeight / 2);
      lookX.set(Math.max(-1, Math.min(1, dx)));
      lookY.set(Math.max(-1, Math.min(1, dy * 0.6)));
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [effectiveState, lookX, lookY]);

  // ── Drag-tilt follow-through (primary roof + badge tilt) ──────
  const tiltMV = useExternalNumber(externalTiltX);
  const roofTilt = useSpring(tiltMV, { stiffness: 90, damping: 16, mass: 1.2 });
  const badgeTilt = useSpring(tiltMV, { stiffness: 70, damping: 14, mass: 1.4 });

  // ── CP-005 · drag-velocity-derived physics (vx + vy → 4 layers) ──
  const vxRaw = useExternalNumber(externalVelocityX);
  const vyRaw = useExternalNumber(externalVelocityY);
  // Clamp to a sane working range. ±2200 px/s covers a fast Mac
  // trackpad fling; beyond that the visual delta caps via Math.min.
  const vxClamped = useTransform(vxRaw, (v) => Math.max(-2200, Math.min(2200, v)));
  const vyClamped = useTransform(vyRaw, (v) => Math.max(-2200, Math.min(2200, v)));
  const speedMV = useTransform(
    [vxClamped, vyClamped],
    ([vx, vy]: number[]) => Math.hypot(vx ?? 0, vy ?? 0),
  );
  // Halo deform (mockup script): haloX = vx*0.012 (cap ±10), haloScaleX
  // grows with speed, haloScaleY shrinks with speed (oval flattening).
  const haloX = useSpring(
    useTransform(vxClamped, (v) => Math.max(-12, Math.min(12, v * 0.012))),
    { stiffness: 140, damping: 18, mass: 1.0 },
  );
  const haloScaleX = useSpring(
    useTransform(speedMV, (s) => 1 + Math.min(0.18, s / 1200)),
    { stiffness: 140, damping: 18, mass: 1.0 },
  );
  const haloScaleY = useSpring(
    useTransform(speedMV, (s) => 1 - Math.min(0.14, s / 1600)),
    { stiffness: 140, damping: 18, mass: 1.0 },
  );
  // Roof lag (mockup): opposes motion, slower spring (mass 1.4).
  const roofLagX = useSpring(
    useTransform(vxClamped, (v) => Math.max(-6, Math.min(6, -v * 0.01))),
    { stiffness: 70, damping: 14, mass: 1.4 },
  );
  const roofLagY = useSpring(
    useTransform(vyClamped, (v) => Math.max(-5, Math.min(5, -v * 0.006))),
    { stiffness: 70, damping: 14, mass: 1.4 },
  );
  // Badge lag (mockup): heaviest, opposite-sign, slowest spring (mass 1.6).
  const badgeLagX = useSpring(
    useTransform(vxClamped, (v) => Math.max(-10, Math.min(10, -v * 0.018))),
    { stiffness: 60, damping: 13, mass: 1.6 },
  );
  const badgeLagY = useSpring(
    useTransform(vyClamped, (v) => Math.max(-10, Math.min(10, -v * 0.014))),
    { stiffness: 60, damping: 13, mass: 1.6 },
  );
  // Body squash (mockup): stretchX = 1 + speed/900 (cap 0.085),
  // stretchY = 1 − speed/1200 (cap 0.06). Reads as "leaning into motion".
  const bodyStretchX = useSpring(
    useTransform(speedMV, (s) => 1 + Math.min(0.085, s / 900)),
    { stiffness: 160, damping: 18, mass: 1.0 },
  );
  const bodyStretchY = useSpring(
    useTransform(speedMV, (s) => 1 - Math.min(0.06, s / 1200)),
    { stiffness: 160, damping: 18, mass: 1.0 },
  );

  // ── State variants (top-level body bob/lean per state) ─────────
  const bodyVariants = {
    idle: { scale: 1, y: 0, rotate: 0, transition: SPRING_BODY },
    listening: { scale: 1.02, y: -2, rotate: -1, transition: SPRING_BODY },
    thinking: { scale: 1.01, y: -1, rotate: 0, transition: SPRING_BODY },
    speaking: { scale: 1.015, y: -1, rotate: 0, transition: SPRING_BODY },
    error: { scale: 1, y: 0, rotate: 0, x: 0, transition: SPRING_TIGHT },
    sleeping: { scale: 0.96, y: 1, rotate: 0, transition: SPRING_BODY },
  } as const;

  // ── Idle floating loop (subtle Y bob; preserved cadence) ──────
  const idleY = useMotionValue(0);
  const idleScale = useMotionValue(1);
  useEffect(() => {
    if (effectiveState === 'sleeping' || reduced) {
      idleY.set(0);
      idleScale.set(1);
      return;
    }
    let alive = true;
    let phase = 0;
    const tick = () => {
      if (!alive) return;
      phase = (phase + 1) % 2;
      // Mockup amplitude: ±2.8px Y desktop; we average to ±2 px for
      // 240-space (the SVG scales down to size). Cadence ~5.4s loop
      // matches a slow, calm "alive but not jittery" vibe.
      idleY.set(phase === 0 ? -2 : 0);
      idleScale.set(phase === 0 ? 1.012 : 1.0);
    };
    tick();
    const id = window.setInterval(tick, 5_400);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [effectiveState, idleY, idleScale, reduced]);
  const breathY = useSpring(idleY, { stiffness: 30, damping: 18, mass: 1.6 });
  const breathScale = useSpring(idleScale, { stiffness: 30, damping: 18, mass: 1.6 });

  // ── State-derived params (halo intensity, eye openness/glow, mouth, error) ──
  const haloOpacity =
    effectiveState === 'speaking' || effectiveState === 'listening'
      ? 0.85
      : effectiveState === 'thinking'
        ? 0.7
        : effectiveState === 'error'
          ? 0.6
          : effectiveState === 'sleeping'
            ? 0.25
            : 0.55;

  const eyesOpenY =
    effectiveState === 'sleeping' ? 0.04 : effectiveState === 'thinking' ? 0.7 : 1;
  const eyeGlow =
    effectiveState === 'speaking' || effectiveState === 'listening'
      ? 1.1
      : effectiveState === 'sleeping'
        ? 0
        : 0.95;

  // Mockup speaking pulse: scale(1.22, 0.78) — mouth squashes vertically
  // and stretches horizontally on each pulse.
  const mouthBaseScaleY =
    effectiveState === 'listening'
      ? 1.18
      : effectiveState === 'error'
        ? 0.45
        : effectiveState === 'sleeping'
          ? 0.4
          : 1;
  const mouthAnimate =
    effectiveState === 'speaking' && !reduced
      ? { scaleY: [1, 0.78, 1, 0.85, 1], scaleX: [1, 1.22, 1, 1.16, 1] }
      : { scaleY: mouthBaseScaleY, scaleX: 1 };
  const mouthTransition: Transition =
    effectiveState === 'speaking' && !reduced
      ? { duration: 0.6, repeat: Infinity, ease: 'easeInOut' }
      : SPRING_BODY;

  // Mockup error shake: translateX -8/7/-5/3 over 420ms. Slightly
  // more pronounced than the prior 100-space shake.
  const errorShake =
    effectiveState === 'error' && !reduced
      ? { x: [0, -8, 7, -5, 3, 0] }
      : { x: 0 };
  const errorTransition: Transition =
    effectiveState === 'error' && !reduced
      ? { duration: 0.42, ease: 'easeInOut' }
      : SPRING_BODY;

  // ── Press feedback ────────────────────────────────────────────
  const [pressed, setPressed] = useState(false);
  const pressStyle: CSSProperties = pressed ? { transform: 'scale(0.96)' } : {};

  const dataMode = mode === 'auto' ? undefined : mode;

  // Per-instance defs IDs (so two Housys on the same page don't share filters).
  const uid = useId().replace(/:/g, '');
  const idBodyGrad = `housy-body-${uid}`;
  const idRoofGrad = `housy-roof-${uid}`;
  const idEyeGrad = `housy-eye-${uid}`;
  const idHaloBlur = `housy-haloblur-${uid}`;
  const idAmberGlow = `housy-amberglow-${uid}`;
  const idSoftGlow = `housy-softglow-${uid}`;

  return (
    <button
      type="button"
      ref={wrapperRef}
      onClick={onClick}
      onPointerDown={(e) => {
        setPressed(true);
        onPointerDown?.(e);
      }}
      onPointerUp={(e) => {
        setPressed(false);
        onPointerUp?.(e);
      }}
      onPointerLeave={() => setPressed(false)}
      aria-label="Housy"
      data-housy-state={effectiveState}
      data-housy-mode={dataMode}
      className={cn(
        'group relative inline-flex items-center justify-center select-none',
        // Tap target stays ≥ 44 px because the wrapping HouseAgent
        // enforces min-w/min-h of 44 px on its drag wrapper. This
        // button itself sizes to `size` (default 72).
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-full',
        className,
      )}
      style={{
        width: size,
        height: size,
        transition: 'transform 140ms cubic-bezier(0.22, 1, 0.36, 1)',
        ...pressStyle,
      }}
    >
      <motion.svg
        viewBox="0 0 240 240"
        width={size}
        height={size}
        style={{
          y: breathY,
          scale: breathScale,
          display: 'block',
          overflow: 'visible',
        }}
        variants={bodyVariants}
        animate={effectiveState === 'error' ? errorShake : effectiveState}
        transition={effectiveState === 'error' ? errorTransition : SPRING_BODY}
      >
        <defs>
          <radialGradient id={idBodyGrad} cx="50%" cy="34%" r="70%">
            <stop offset="0" className="housy-stop-body-hi" />
            <stop offset="0.38" className="housy-stop-body-a" />
            <stop offset="0.7" className="housy-stop-body-b" />
            <stop offset="1" className="housy-stop-body-c" />
          </radialGradient>
          <linearGradient
            id={idRoofGrad}
            x1="30"
            y1="50"
            x2="210"
            y2="126"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" className="housy-stop-roof-a" />
            <stop offset="1" className="housy-stop-roof-b" />
          </linearGradient>
          <radialGradient id={idEyeGrad} cx="50%" cy="50%" r="62%">
            <stop offset="0" className="housy-stop-eye-hi" />
            <stop offset="0.48" className="housy-stop-eye-mid" />
            <stop offset="1" className="housy-stop-eye-lo" />
          </radialGradient>
          <filter id={idSoftGlow} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1 0 0 0 0.58  0 1 0 0 0.35  0 0 1 0 1  0 0 0 0.7 0"
              result="glow"
            />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={idAmberGlow} x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="4.5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="1 0 0 0 1  0 1 0 0 0.52  0 0 1 0 0.08  0 0 0 0.95 0"
              result="glow"
            />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={idHaloBlur} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" />
          </filter>
          <style>{`
            /* CP-005 violet/amber palette from the reference lamina. */
            .housy-stop-body-hi { stop-color: #E6DCFF; stop-opacity: 0.95; }
            .housy-stop-body-a  { stop-color: #A68AFF; }
            .housy-stop-body-b  { stop-color: #5F45D8; }
            .housy-stop-body-c  { stop-color: #241847; }
            .housy-stop-roof-a  { stop-color: #25284F; }
            .housy-stop-roof-b  { stop-color: #12152F; }
            .housy-stop-eye-hi  { stop-color: #FFF7D8; }
            .housy-stop-eye-mid { stop-color: #FFD49B; }
            .housy-stop-eye-lo  { stop-color: #FF9F2E; }
            .housy-body-overlay-hi { fill: rgba(255,255,255,0.08); }
            .housy-body-overlay-lo { fill: rgba(25,14,50,0.28); }
            .housy-roof-rim    { stroke: rgba(255,255,255,0.26); stroke-width: 5; fill: none; stroke-linecap: round; }
            .housy-chimney-fill { fill: #1B1F40; }
            .housy-chimney-inner{ fill: rgba(255,255,255,0.09); }
            .housy-eye-pupil    { fill: #FFF9DF; opacity: 0.95; }
            .housy-mouth-shell  { fill: #2A1934; }
            .housy-mouth-amber  { fill: #FF9F2E; }
            .housy-mouth-shine  { fill: rgba(255,255,255,0.12); }
            .housy-halo-outer   { stroke: #A76DFF; stroke-width: 5; fill: none; opacity: 0.84; }
            .housy-halo-inner   { fill: #B580FF; opacity: 0.34; }
            .housy-listen-ring  { stroke: #A98CFF; stroke-width: 4; fill: none; stroke-linecap: round; }
            .housy-sleep-eye    { stroke: #FFD49B; stroke-width: 6; fill: none; stroke-linecap: round; }
            .housy-badge-outer  { fill: #FF8E1F; }
            .housy-badge-inner  { fill: #FF9F2E; stroke: rgba(255,255,255,0.72); stroke-width: 4; }
            .housy-badge-text   { fill: #FFFFFF; font-weight: 800; }
            .housy-thought-bg   { fill: #E6DCFF; opacity: 0.95; }
            .housy-thought-dot  { fill: #40376F; }
            .housy-error-spark  { stroke: #FF554A; stroke-width: 4; stroke-linecap: round; fill: none; }
            .housy-zzz          { fill: #EEE8FF; font-weight: 900; }

            /* Light-mode tweaks (mockup body.light overrides). */
            .light .housy-stop-body-hi { stop-color: #FFFFFF; stop-opacity: 0.95; }
            .light .housy-stop-body-a  { stop-color: #D9D0FF; }
            .light .housy-stop-body-b  { stop-color: #A58CFF; }
            .light .housy-stop-body-c  { stop-color: #6C54C9; }
            .light .housy-stop-roof-a  { stop-color: #4B517B; }
            .light .housy-stop-roof-b  { stop-color: #252843; }
            .light .housy-body-overlay-hi { fill: rgba(255,255,255,0.18); }
            .light .housy-body-overlay-lo { fill: rgba(80,46,160,0.18); }
            .light .housy-chimney-fill    { fill: #2C3052; }
            .light .housy-chimney-inner   { fill: rgba(255,255,255,0.18); }

            /* data-housy-mode forces light/dark independent of ancestor class. */
            [data-housy-mode="light"] .housy-stop-body-hi { stop-color: #FFFFFF !important; stop-opacity: 0.95 !important; }
            [data-housy-mode="light"] .housy-stop-body-a  { stop-color: #D9D0FF !important; }
            [data-housy-mode="light"] .housy-stop-body-b  { stop-color: #A58CFF !important; }
            [data-housy-mode="light"] .housy-stop-body-c  { stop-color: #6C54C9 !important; }
            [data-housy-mode="light"] .housy-stop-roof-a  { stop-color: #4B517B !important; }
            [data-housy-mode="light"] .housy-stop-roof-b  { stop-color: #252843 !important; }
            [data-housy-mode="dark"]  .housy-stop-body-hi { stop-color: #E6DCFF !important; stop-opacity: 0.95 !important; }
            [data-housy-mode="dark"]  .housy-stop-body-a  { stop-color: #A68AFF !important; }
            [data-housy-mode="dark"]  .housy-stop-body-b  { stop-color: #5F45D8 !important; }
            [data-housy-mode="dark"]  .housy-stop-body-c  { stop-color: #241847 !important; }
            [data-housy-mode="dark"]  .housy-stop-roof-a  { stop-color: #25284F !important; }
            [data-housy-mode="dark"]  .housy-stop-roof-b  { stop-color: #12152F !important; }
          `}</style>
        </defs>

        {/* HALO PLATFORM (back-most) — mockup cx=120 cy=197 rx=56 outer / rx=38 inner.
            CP-005 drag-physics: deforms via haloX (translate) + haloScaleX/Y
            (oval flatten) when the parent passes drag velocity. */}
        <motion.g
          style={{
            transformOrigin: '120px 197px',
            x: haloX,
            scaleX: haloScaleX,
            scaleY: haloScaleY,
          }}
          animate={
            reduced
              ? { opacity: haloOpacity }
              : effectiveState === 'speaking' || effectiveState === 'listening'
                ? { opacity: [haloOpacity, haloOpacity * 1.15, haloOpacity] }
                : { opacity: haloOpacity }
          }
          transition={
            effectiveState === 'speaking' || effectiveState === 'listening'
              ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
              : SPRING_BODY
          }
        >
          <ellipse
            cx="120"
            cy="197"
            rx="56"
            ry="10"
            className="housy-halo-outer"
            filter={`url(#${idHaloBlur})`}
          />
          <ellipse cx="120" cy="197" rx="38" ry="5" className="housy-halo-inner" />
        </motion.g>

        {/* LISTENING RINGS (mockup paths M53 111 / M187 111). Mounted
            outside the body so the body squash/stretch doesn't deform them. */}
        <AnimatePresence>
          {effectiveState === 'listening' && !reduced && (
            <motion.g
              key="listening-rings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.path
                d="M53 111 C38 123 38 147 53 159"
                className="housy-listen-ring"
                animate={{ opacity: [0.12, 0.72, 0.12], scaleX: [0.88, 1.08, 0.88] }}
                transition={{ duration: 1.45, repeat: Infinity, ease: 'easeInOut' }}
                style={{ transformOrigin: '46px 135px' }}
              />
              <motion.path
                d="M187 111 C202 123 202 147 187 159"
                className="housy-listen-ring"
                animate={{ opacity: [0.12, 0.72, 0.12], scaleX: [0.88, 1.08, 0.88] }}
                transition={{ duration: 1.45, repeat: Infinity, ease: 'easeInOut', delay: 0.18 }}
                style={{ transformOrigin: '194px 135px' }}
              />
            </motion.g>
          )}
        </AnimatePresence>

        {/* BODY — mockup main path + inner highlight + bottom shadow.
            CP-005 drag-physics: scaleX/scaleY squash anchored at body
            centroid (120, 125). Body fades subtly when sleeping. */}
        <motion.g
          style={{
            transformOrigin: '120px 125px',
            scaleX: bodyStretchX,
            scaleY: bodyStretchY,
          }}
        >
          <path
            d="M58 103 C61 73 83 57 120 57 C157 57 179 73 182 103 L182 138 C182 178 154 194 120 194 C86 194 58 178 58 138 Z"
            fill={`url(#${idBodyGrad})`}
            filter={`url(#${idSoftGlow})`}
            opacity={effectiveState === 'sleeping' ? 0.78 : 1}
          />
          <path
            d="M63 108 C66 80 87 66 120 66 C153 66 174 80 177 108 L177 137 C177 170 152 185 120 185 C88 185 63 170 63 137 Z"
            className="housy-body-overlay-hi"
          />
          <path
            d="M69 145 C86 177 155 177 171 145 C166 176 145 190 120 190 C95 190 74 176 69 145 Z"
            className="housy-body-overlay-lo"
          />
        </motion.g>

        {/* ROOF + CHIMNEY — mockup roof path M37 113 + chimney 156×61.
            CP-005 drag-physics: roofLagX/Y translates the whole roof+chimney
            opposite the motion vector; roofTilt rotates from primary tilt;
            thinking state adds a slow ±1.5deg rock. */}
        <motion.g
          style={{
            transformOrigin: '120px 91px',
            x: roofLagX,
            y: roofLagY,
            rotate: roofTilt,
          }}
          animate={
            effectiveState === 'thinking' && !reduced
              ? { y: [0, -3, 0], rotate: [0, -1.5, 0] }
              : undefined
          }
          transition={
            effectiveState === 'thinking' && !reduced
              ? { duration: 1.9, repeat: Infinity, ease: 'easeInOut' }
              : SPRING_BODY
          }
        >
          <rect
            x="156"
            y="61"
            width="22"
            height="45"
            rx="5"
            className="housy-chimney-fill"
            opacity="0.95"
          />
          <rect
            x="161"
            y="66"
            width="12"
            height="30"
            rx="3"
            className="housy-chimney-inner"
          />
          <path
            d="M37 113 C70 81 94 57 120 43 C146 57 170 81 203 113 C204 116 202 121 197 122 L43 122 C38 121 36 116 37 113 Z"
            fill={`url(#${idRoofGrad})`}
            filter={`url(#${idSoftGlow})`}
          />
          <path
            d="M52 109 C83 82 100 66 120 56 C140 66 157 82 188 109"
            className="housy-roof-rim"
          />
        </motion.g>

        {/* OPEN EYES (mockup x=83 / x=137, w=20 h=25 rx=5; pupil 8×8 at 88×130 / 142×130). */}
        <motion.g
          filter={`url(#${idAmberGlow})`}
          animate={{
            opacity: effectiveState === 'sleeping' ? 0 : eyeGlow,
            scaleY: eyesOpenY,
          }}
          transition={SPRING_BODY}
          style={{ transformOrigin: '120px 138px' }}
        >
          <rect x="83" y="126" width="20" height="25" rx="5" fill={`url(#${idEyeGrad})`} />
          <rect x="137" y="126" width="20" height="25" rx="5" fill={`url(#${idEyeGrad})`} />
          <motion.rect
            x="88"
            y="130"
            width="8"
            height="8"
            rx="2"
            className="housy-eye-pupil"
            style={{ x: pupilTX, y: pupilTY }}
          />
          <motion.rect
            x="142"
            y="130"
            width="8"
            height="8"
            rx="2"
            className="housy-eye-pupil"
            style={{ x: pupilTX, y: pupilTY }}
          />
        </motion.g>

        {/* SLEEP EYES (mockup arcs M82 138 / M134 138). */}
        <AnimatePresence>
          {effectiveState === 'sleeping' && (
            <motion.g
              key="sleep-eyes"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              filter={`url(#${idAmberGlow})`}
            >
              <path d="M82 138 C90 146 99 146 106 138" className="housy-sleep-eye" />
              <path d="M134 138 C142 146 151 146 158 138" className="housy-sleep-eye" />
            </motion.g>
          )}
        </AnimatePresence>

        {/* MOUTH — shell + amber tongue + shine (mockup x=104 y=158 w=32 h=25 rx=8).
            Speaking state pulses scaleX/scaleY (mockup keyframes). */}
        <motion.g
          filter={`url(#${idAmberGlow})`}
          style={{ transformOrigin: '120px 170.5px' }}
          animate={mouthAnimate}
          transition={mouthTransition}
        >
          <rect x="104" y="158" width="32" height="25" rx="8" className="housy-mouth-shell" />
          <rect x="113" y="170" width="12" height="7" rx="2" className="housy-mouth-amber" />
          <rect x="109" y="162" width="22" height="10" rx="4" className="housy-mouth-shine" />
        </motion.g>

        {/* BADGE — chimney corner (mockup cx=176 cy=64 r=25 outer / r=20 inner).
            CP-005 drag-physics: badgeLagX/Y translates opposite motion (heaviest
            spring), badgeTilt rotates from primary tilt. */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.g
              key={`badge-${unreadCount}`}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={SPRING_TIGHT}
              style={{
                transformOrigin: '176px 64px',
                rotate: badgeTilt,
                x: badgeLagX,
                y: badgeLagY,
              }}
            >
              <circle cx="176" cy="64" r="25" className="housy-badge-outer" />
              <circle cx="176" cy="64" r="20" className="housy-badge-inner" />
              <text
                x="176"
                y="73"
                textAnchor="middle"
                fontSize="28"
                className="housy-badge-text"
                style={{ fontFamily: 'inherit', userSelect: 'none' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </text>
            </motion.g>
          )}
        </AnimatePresence>

        {/* THINKING — thought bubble at top-right (mockup M132 34, dots cy=52). */}
        <AnimatePresence>
          {effectiveState === 'thinking' && !reduced && (
            <motion.g
              key="thinking-bubble"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <path
                d="M132 34 h32 a13 13 0 0 1 13 13 v9 a13 13 0 0 1-13 13 h-19 l-14 13 4-13 h-3 a13 13 0 0 1-13-13 v-9 a13 13 0 0 1 13-13z"
                className="housy-thought-bg"
                filter={`url(#${idSoftGlow})`}
              />
              {[0, 1, 2].map((i) => (
                <motion.circle
                  key={i}
                  cx={139 + i * 11}
                  cy="52"
                  r="3"
                  className="housy-thought-dot"
                  animate={{ opacity: [0.25, 1, 0.25], y: [0, -1.5, 0] }}
                  transition={{
                    duration: 1.05,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.15,
                  }}
                />
              ))}
            </motion.g>
          )}
        </AnimatePresence>

        {/* ERROR sparks (mockup 4 strokes around 185×91 / 197×101 / 63×91 / 51×101). */}
        <AnimatePresence>
          {effectiveState === 'error' && !reduced && (
            <motion.g
              key="error-sparks"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[
                'M185 91 l8 -8',
                'M197 101 l10 -2',
                'M63 91 l-8 -8',
                'M51 101 l-10 -2',
              ].map((d, i) => (
                <motion.path
                  key={i}
                  d={d}
                  className="housy-error-spark"
                  animate={{ opacity: [0.18, 1, 0.18], y: [0, -2, 0] }}
                  transition={{
                    duration: 0.82,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.06,
                  }}
                />
              ))}
            </motion.g>
          )}
        </AnimatePresence>

        {/* SLEEPING zZz (mockup 176×51 / 190×39 / 207×24, sizes 18/24/30). */}
        <AnimatePresence>
          {effectiveState === 'sleeping' && !reduced && (
            <motion.g
              key="zzz"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[
                { x: 176, y: 51, fontSize: 18, ch: 'z', delay: 0 },
                { x: 190, y: 39, fontSize: 24, ch: 'Z', delay: 0.5 },
                { x: 207, y: 24, fontSize: 30, ch: 'Z', delay: 1.0 },
              ].map((z, i) => (
                <motion.text
                  key={i}
                  x={z.x}
                  y={z.y}
                  fontSize={z.fontSize}
                  className="housy-zzz"
                  style={{ fontFamily: 'inherit' }}
                  animate={{
                    opacity: [0, 1, 0],
                    y: [10, 0, -16],
                    scale: [0.88, 1, 1.08],
                  }}
                  transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: z.delay,
                  }}
                >
                  {z.ch}
                </motion.text>
              ))}
            </motion.g>
          )}
        </AnimatePresence>
      </motion.svg>

      {/* Wave 380 · FIX 4 · floating emoji reaction overlay.
          Sits above the chimney (~ -28 px from button top), pointer
          events disabled, mode="popLayout" so transitions between
          glyphs swap without an overlap flash. Asymmetric timing:
          400 ms strong ease-out enter, 180 ms ease-out exit (snappier
          out — Emil aligned). Skipped entirely under reduced motion. */}
      <AnimatePresence mode="popLayout">
        {reactionEmoji && (
          <motion.span
            key={reactionEmoji}
            aria-hidden
            className="pointer-events-none absolute select-none"
            style={{
              top: -28,
              left: '50%',
              fontSize: 18,
              lineHeight: 1,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
            }}
            initial={{ opacity: 0, y: 4, scale: 0.8, x: '-50%' }}
            animate={{ opacity: 1, y: -8, scale: 1, x: '-50%' }}
            exit={{
              opacity: 0,
              y: -16,
              scale: 0.9,
              x: '-50%',
              transition: { duration: 0.18, ease: 'easeOut' },
            }}
            transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
          >
            {reactionEmoji}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
